/* eslint-disable @typescript-eslint/no-unused-vars */

/**
 * Radar↔Camera batch extrinsic fitter (constrained).
 *
 * What it does:
 *  - Each frame, associates RADAR TRACKS -> CAMERA DETECTIONS (no camera tracks required)
 *    using current extrinsic guess by projecting each track into image and picking the closest detection
 *    (with gating).
 *  - Accumulates (ground point, image point) correspondences.
 *  - When you call fitBatch(), it optimizes a small parameter set (yaw + small pitch correction by default)
 *    to minimize reprojection error, with robust trimming.
 *
 * Coordinate conventions used (matches what you provided):
 *  - World / Device ground frame: X = right, Y = forward, Z = up (meters)
 *  - Camera pinhole projection frame: X = right, Y = down, Z = forward (standard)
 *  - Fixed remap device/world -> camera axes:
 *        Xc = Xw
 *        Yc = -Zw
 *        Zc = Yw
 *
 * IMU angles you provided:
 *  - pitch0: radians (negative means looking down)
 *  - roll0:  radians
 *  - yaw is NOT observable from accelerometer; we estimate yaw in this fitter.
 *
 * Lens distortion:
 *  - OpenCV 5-coeff model [k1, k2, p1, p2, k3]
 *
 * Notes:
 *  - This class fits a camera pose relative to the ground frame at the sensor origin with height h.
 *  - If your camera optical center is not exactly at (0,0,h) in your ground frame, add translation later.
 *  - If you are drawing on a scaled/cropped image, ensure intrinsics/distortion correspond to the SAME
 *    pixel coordinate system as detections.
 */
import { InferredObjectTlvInfo } from "./InferredObjectTlvInfo";
import { PointCloudSpherical } from "./PointCloudSpherical";
import { TrackState } from "./TrackState";

// ---------- Types ----------
type Vec3 = { x: number; y: number; z: number };
type Mat3 = number[][]; // 3x3
type Intrinsics = { fx: number; fy: number; cx: number; cy: number };
type DistCoeffs = { k1: number; k2: number; p1: number; p2: number; k3: number };

type Sample = {
    tMs: number;
    trackId: number;
    // ground/world position (meters), Z up
    Pw: Vec3;
    // measured image point (pixels)
    uMeas: number;
    vMeas: number;
    // optional weighting
    weight: number;
};

export type FitReport = {
    usedSamples: number;
    droppedAsOutliers: number;
    rmsePx: number;
    meanPx: number;
    medianPx: number;
    maxPx: number;

    // fitted params (radians)
    yawRad: number;
    pitchRad: number;
    rollRad: number;
    heightM: number;

    // per-sample errors (pixels) for samples that were used
    errorsPx: number[];
};

export type FitterParams = {
    // initial IMU attitude
    pitch0Rad: number;
    roll0Rad: number;

    // sensor height above ground (meters)
    heightM: number;

    // gating for per-frame association
    maxPixelGate: number; // e.g., 120 px

    // minimum confidence for detections to be considered
    minDetectionConfidence: number; // e.g., 0.25

    // minimum confidence for radar tracks to be considered
    minTrackConfidence: number; // e.g., 0.2

    // how many samples to retain
    maxSamples: number; // e.g., 4000

    // robust trimming for fitting
    // (after computing errors, keep those with error <= trimPx)
    trimPx: number; // e.g., 60

    // optimize which parameters
    optimizePitch: boolean;
    optimizeRoll: boolean;
    optimizeHeight: boolean;
};

// ---------- Math helpers ----------
function clamp(x: number, a: number, b: number): number {
    return Math.max(a, Math.min(b, x));
}
function deg2rad(d: number): number {
    return (d * Math.PI) / 180;
}

function mulMat3Vec3(R: Mat3, p: Vec3): Vec3 {
    return {
        x: R[0][0] * p.x + R[0][1] * p.y + R[0][2] * p.z,
        y: R[1][0] * p.x + R[1][1] * p.y + R[1][2] * p.z,
        z: R[2][0] * p.x + R[2][1] * p.y + R[2][2] * p.z,
    };
}

function matMul3(A: Mat3, B: Mat3): Mat3 {
    const out: Mat3 = [
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
    ];
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            out[i][j] = A[i][0] * B[0][j] + A[i][1] * B[1][j] + A[i][2] * B[2][j];
        }
    }
    return out;
}

function transpose3(R: Mat3): Mat3 {
    return [
        [R[0][0], R[1][0], R[2][0]],
        [R[0][1], R[1][1], R[2][1]],
        [R[0][2], R[1][2], R[2][2]],
    ];
}

// Rotations around WORLD axes (X right, Y forward, Z up).
// We’ll use these as EXTRINSIC rotations applied in order: yaw(Z), pitch(X), roll(Y).
// This is a reasonable convention for a fixed camera where yaw is heading around Z,
// pitch is tilt around X (right), and roll is tilt around Y (forward).
function Rz(yaw: number): Mat3 {
    const c = Math.cos(yaw),
        s = Math.sin(yaw);
    return [
        [c, -s, 0],
        [s, c, 0],
        [0, 0, 1],
    ];
}
function Rx(pitch: number): Mat3 {
    const c = Math.cos(pitch),
        s = Math.sin(pitch);
    return [
        [1, 0, 0],
        [0, c, -s],
        [0, s, c],
    ];
}
function Ry(roll: number): Mat3 {
    const c = Math.cos(roll),
        s = Math.sin(roll);
    return [
        [c, 0, s],
        [0, 1, 0],
        [-s, 0, c],
    ];
}

// OpenCV distortion on normalized coords
function distortNormalized(x: number, y: number, d: DistCoeffs): { xd: number; yd: number } {
    const { k1, k2, p1, p2, k3 } = d;
    const r2 = x * x + y * y;
    const r4 = r2 * r2;
    const r6 = r4 * r2;

    const radial = 1 + k1 * r2 + k2 * r4 + k3 * r6;

    const x_tan = 2 * p1 * x * y + p2 * (r2 + 2 * x * x);
    const y_tan = p1 * (r2 + 2 * y * y) + 2 * p2 * x * y;

    return { xd: x * radial + x_tan, yd: y * radial + y_tan };
}

function undistortNormalized(xd: number, yd: number, d: DistCoeffs, iterations = 8): { x: number; y: number } | null {
    if (!isFinite(xd) || !isFinite(yd)) return null;
    let x = xd;
    let y = yd;
    for (let i = 0; i < iterations; i++) {
        const { xd: xdn, yd: ydn } = distortNormalized(x, y, d);
        const ex = xd - xdn;
        const ey = yd - ydn;
        x += ex;
        y += ey;
    }
    return { x, y };
}

function projectCamToPixelDistorted(pCam: Vec3, intr: Intrinsics, dist: DistCoeffs): { u: number; v: number } | null {
    const { x: Xc, y: Yc, z: Zc } = pCam;
    if (!isFinite(Zc) || Zc <= 1e-6) return null;

    const x = Xc / Zc;
    const y = Yc / Zc;

    const { xd, yd } = distortNormalized(x, y, dist);

    const u = intr.fx * xd + intr.cx;
    const v = intr.fy * yd + intr.cy;

    if (!isFinite(u) || !isFinite(v)) return null;
    return { u, v };
}

// Median utility
function median(arr: number[]): number {
    if (arr.length === 0) return NaN;
    const a = arr.slice().sort((x, y) => x - y);
    const mid = Math.floor(a.length / 2);
    return a.length % 2 === 0 ? 0.5 * (a[mid - 1] + a[mid]) : a[mid];
}

// ---------- Main class ----------
export class RadarCameraExtrinsicFitter {
    public isFit: boolean;
    private intr: Intrinsics;
    private dist: DistCoeffs;
    private params: FitterParams;
    private minSamplesToFit: number = 80;

    // current estimate
    private yawRad: number = 0;
    private pitchRad: number;
    private rollRad: number;

    // sample buffer
    private samples: Sample[] = [];

    // Fixed remap: world/device (X right, Y forward, Z up) -> camera (X right, Y down, Z forward)
    private readonly R_cam_from_world_axes: Mat3 = [
        [1, 0, 0],
        [0, 0, -1],
        [0, 1, 0],
    ];

    constructor(intr: Intrinsics, dist: DistCoeffs, userParams: Partial<FitterParams>) {
        this.intr = intr;
        this.dist = dist;
        this.isFit = false;

        const defaults: FitterParams = {
            pitch0Rad: 0,
            roll0Rad: 0,
            heightM: 1,
            maxPixelGate: 140,
            minDetectionConfidence: 0.25,
            minTrackConfidence: 0.2,
            maxSamples: 4000,
            trimPx: 60,
            optimizePitch: true,
            optimizeRoll: true,
            optimizeHeight: true,
        };

        this.params = { ...defaults, ...userParams } as FitterParams;

        this.pitchRad = this.params.pitch0Rad;
        this.rollRad = this.params.roll0Rad;
    }

    public setDeviceHeight(newHeight: number): void {
        this.isFit = false;
        this.params.heightM = newHeight;
    }
    public isReadyToFit(): boolean {
        return !this.isFit && this.samples.length >= this.minSamplesToFit;
    }
    /** Optionally set an initial yaw guess (radians). */
    public setInitialYaw(yawRad: number): void {
        this.yawRad = yawRad;
    }

    /**
     * Seed all four pose parameters at once (yaw / pitch / roll in radians,
     * height in meters). Call this before `addFrame()` to make per-frame radar
     * → detection association use the user's current manual values, and to
     * give `fitBatch()` a starting point in the right basin. Resets `isFit`
     * since the prior fit no longer reflects the seeded pose.
     */
    public setInitialEstimate(yawRad: number, pitchRad: number, rollRad: number, heightM: number): void {
        this.yawRad = yawRad;
        this.pitchRad = pitchRad;
        this.rollRad = rollRad;
        this.params.heightM = heightM;
        this.isFit = false;
    }

    /** Current estimated angles (radians). */
    public getEstimate(): { yawRad: number; pitchRad: number; rollRad: number; heightM: number } {
        return { yawRad: this.yawRad, pitchRad: this.pitchRad, rollRad: this.rollRad, heightM: this.params.heightM };
    }

    /** Number of accumulated samples available for evaluation/fitting. */
    public getSampleCount(): number {
        return this.samples.length;
    }

    /** Minimum accumulated samples `fitBatch()` requires before it will attempt a fit. */
    public getMinSamplesToFit(): number {
        return this.minSamplesToFit;
    }

    /**
     * Evaluate reprojection error against the accumulated samples at the given
     * pose, without modifying the fitter's stored estimate. Used for live UI
     * readout when the user manually tweaks calibration values.
     */
    public evaluateAt(yawRad: number, pitchRad: number, rollRad: number, heightM: number):
        { rmsePx: number; meanPx: number; medianPx: number; maxPx: number; usedSamples: number; droppedAsOutliers: number } | null {
        if (this.samples.length === 0) return null;

        const errs: number[] = [];
        for (const s of this.samples) {
            const proj = this.projectRadarPointToImage(s.Pw, yawRad, pitchRad, rollRad, heightM);
            if (!proj) continue;
            errs.push(Math.hypot(proj.u - s.uMeas, proj.v - s.vMeas));
        }
        if (errs.length === 0) return null;

        const inliers = errs.filter((e) => e <= this.params.trimPx);
        if (inliers.length === 0) {
            return {
                rmsePx: NaN, meanPx: NaN, medianPx: NaN, maxPx: NaN,
                usedSamples: 0, droppedAsOutliers: errs.length,
            };
        }

        const rmse = Math.sqrt(inliers.reduce((a, e) => a + e * e, 0) / inliers.length);
        const mean = inliers.reduce((a, e) => a + e, 0) / inliers.length;
        const result = {
            rmsePx: rmse,
            meanPx: mean,
            medianPx: median(inliers),
            maxPx: Math.max(...inliers),
            usedSamples: inliers.length,
            droppedAsOutliers: errs.length - inliers.length,
        };
        console.log("[Calib] evaluateAt", {
            yawDeg: (yawRad * 180) / Math.PI,
            pitchDeg: (pitchRad * 180) / Math.PI,
            rollDeg: (rollRad * 180) / Math.PI,
            heightM,
            ...result,
            totalSamples: this.samples.length,
        });
        return result;
    }

    /** Clears all accumulated samples. */
    public clear(): void {
        this.samples = [];
    }

    /**
     * Add one frame worth of data.
     *
     * You provide:
     *  - tracks: radar tracker tracks in ground/world frame (X right, Y forward, Z up)
     *  - detections: VA boxes in image pixel coordinates (same pixel frame as intr/dist)
     *
     * You ALSO provide radar point clouds + association array; you don't need them for this minimal pipeline.
     * (But you can use them later to refine gating/association. For now, we use track positions.)
     */
    public addFrame(
        tMs: number,
        pointClouds: PointCloudSpherical[],
        tracks: TrackState[],
        pointToTrackIndex: number[],
        detections: InferredObjectTlvInfo[]
    ): void {
        // Filter candidates
        const dets = detections.filter((d) => d.confidence >= this.params.minDetectionConfidence);

        const trackCands = tracks
            .map((tr, idx) => ({ tr, idx }))
            .filter((x) => x.tr.confidence >= this.params.minTrackConfidence);

        if (dets.length === 0 || trackCands.length === 0) return;

        // Precompute measurement points (bottom-center)
        const detMeas = dets.map((d) => ({
            det: d,
            u: d.x + d.width * 0.5,
            v: d.y + d.height * .75,
        }));

        // Project each track under current pose and pick best detection
        // One-to-one greedy assignment (works surprisingly well with decent gating).
        const usedDet = new Set<bigint>();

        for (const { tr } of trackCands) {
            // Only use ground-ish targets; if your tracker gives Z, keep it, but vehicles are near Z=0.
            const Pw: Vec3 = { x: tr.positionX, y: tr.positionY, z: tr.positionZ };

            const proj = this.radarPointToImageEstimate(Pw.x, Pw.y, Pw.z);
            if (!proj) continue;

            // find closest detection by pixel distance (to bottom-center)
            let best: { det: InferredObjectTlvInfo; u: number; v: number; d2: number } | null = null;

            for (const m of detMeas) {
                if (usedDet.has(m.det.id)) continue;

                const du = m.u - proj.u;
                const dv = m.v - proj.v;
                const d2 = du * du + dv * dv;

                if (d2 <= this.params.maxPixelGate * this.params.maxPixelGate) {
                    if (!best || d2 < best.d2) best = { det: m.det, u: m.u, v: m.v, d2 };
                }
            }

            if (!best) continue;

            usedDet.add(best.det.id);

            // Weight: you can incorporate det confidence + track confidence
            const w = clamp(best.det.confidence, 0.0, 1.0) * clamp(tr.confidence, 0.0, 1.0);

            this.samples.push({
                tMs,
                trackId: tr.trackId,
                Pw,
                uMeas: best.u,
                vMeas: best.v,
                weight: w,
            });
        }

        // keep buffer bounded
        if (this.samples.length > this.params.maxSamples) {
            // drop oldest
            this.samples.splice(0, this.samples.length - this.params.maxSamples);
        }
    }

    /**
     * Batch fit yaw + (optionally) pitch/roll.
     *
     * Returns a report including RMSE on the inlier set used for fitting.
     *
     * This uses a robust coordinate-descent style search (no external deps).
     * It’s designed to be stable and simple to tune.
     */
    public fitBatch(): FitReport | null {
        if (this.samples.length < this.minSamplesToFit) return null;

        const r2d = (r: number) => (r * 180) / Math.PI;

        // Start from current estimate (pitch/roll anchored to IMU by default).
        let yaw = this.yawRad;
        let pitch = this.pitchRad;
        let roll = this.rollRad;
        let height = this.params.heightM;

        // Height box constraint: ±20% of the user-supplied height.
        const heightLo = this.params.heightM * 0.8;
        const heightHi = this.params.heightM * 1.2;

        // Yaw box constraint: the camera is rigidly co-mounted with the radar,
        // so yaw cannot drift far from the initial guess. ±2° is plenty.
        const yawLo = this.yawRad - deg2rad(2);
        const yawHi = this.yawRad + deg2rad(2);

        console.log("[Fit] start", {
            samples: this.samples.length,
            yaw0Deg: r2d(yaw),
            pitch0Deg: r2d(pitch),
            roll0Deg: r2d(roll),
            heightM: height,
            accelPitchDeg: r2d(this.params.pitch0Rad),
            accelRollDeg: r2d(this.params.roll0Rad),
            yawBoundsDeg: [r2d(yawLo), r2d(yawHi)],
            heightBoundsM: [heightLo, heightHi],
            optimizePitch: this.params.optimizePitch,
            optimizeRoll: this.params.optimizeRoll,
            optimizeHeight: this.params.optimizeHeight,
            trimPx: this.params.trimPx,
        });

        // Helper: evaluate loss & gather errors at a 4-tuple (yaw, pitch, roll, height)
        const evalAt = (yawTest: number, pitchTest: number, rollTest: number, heightTest: number) => {
            const errs: number[] = [];
            const wts: number[] = [];
            for (const s of this.samples) {
                const proj = this.projectRadarPointToImage(s.Pw, yawTest, pitchTest, rollTest, heightTest);
                if (!proj) continue;
                const du = proj.u - s.uMeas;
                const dv = proj.v - s.vMeas;
                const e = Math.hypot(du, dv);
                errs.push(e);
                wts.push(Math.max(1e-6, s.weight));
            }

            if (errs.length === 0) {
                return { loss: Number.POSITIVE_INFINITY, errs: [], inlierIdx: [] as number[] };
            }

            // Huber-style robust loss: e <= trimPx contributes e², beyond that
            // it grows linearly (trimPx * (2e - trimPx)), differentiable at the
            // boundary so the objective stays smooth across parameter changes.
            const trim = this.params.trimPx;
            const inlierIdx: number[] = [];
            let loss = 0;
            for (let i = 0; i < errs.length; i++) {
                const e = errs[i];
                const huber = e <= trim ? e * e : trim * (2 * e - trim);
                loss += wts[i] * huber;
                if (e <= trim) inlierIdx.push(i);
            }

            if (inlierIdx.length < Math.max(20, Math.floor(0.2 * errs.length))) {
                return { loss: Number.POSITIVE_INFINITY, errs, inlierIdx: [] as number[] };
            }

            loss /= errs.length;
            return { loss, errs, inlierIdx };
        };

        // Initial joint coarse grid search over (yaw, pitch). Pitch and yaw are
        // coupled, so a pitch-only sweep at the wrong yaw lands on the wrong
        // pitch and traps the local optimizer in a bad basin. Yaw range mirrors
        // the ±2° hardware constraint; pitch covers [0, 2*pitch0].
        if (this.params.optimizePitch) {
            const pitchStep = deg2rad(1);
            const yawStep = deg2rad(0.5);
            const p0 = this.params.pitch0Rad;
            const pLo = Math.min(0, 2 * p0);
            const pHi = Math.max(0, 2 * p0);

            let bestPitch = pitch;
            let bestYaw = yaw;
            let bestLoss = evalAt(yaw, pitch, roll, height).loss;

            const gridResults: { yawDeg: number; pitchDeg: number; loss: number; inliers: number }[] = [];

            for (let p = pLo; p <= pHi; p += pitchStep) {
                for (let y = yawLo; y <= yawHi + 1e-9; y += yawStep) {
                    const ev = evalAt(y, p, roll, height);
                    gridResults.push({ yawDeg: r2d(y), pitchDeg: r2d(p), loss: ev.loss, inliers: ev.inlierIdx.length });
                    if (ev.loss < bestLoss) {
                        bestLoss = ev.loss;
                        bestPitch = p;
                        bestYaw = y;
                    }
                }
                if (pitchStep <= 0) break;
            }
            pitch = bestPitch;
            yaw = bestYaw;

            gridResults.sort((a, b) => a.loss - b.loss);
            console.log("[Fit] grid range", {
                pitchRangeDeg: [r2d(pLo), r2d(pHi)],
                pitchStepDeg: r2d(pitchStep),
                yawRangeDeg: [r2d(yawLo), r2d(yawHi)],
                yawStepDeg: r2d(yawStep),
                evaluations: gridResults.length,
            });
            console.log("[Fit] grid top 15 (lowest loss first)", gridResults.slice(0, 15));
            console.log("[Fit] grid winner", { yawDeg: r2d(bestYaw), pitchDeg: r2d(bestPitch), loss: bestLoss });
        }

        // Build the parameter vector for joint Nelder-Mead optimization. Yaw is
        // always optimized; pitch/roll/height are gated by the optimize flags.
        type Slot = "yaw" | "pitch" | "roll" | "height";
        const slots: Slot[] = ["yaw"];
        const x0: number[] = [yaw];
        const stepInit: number[] = [deg2rad(2)];

        if (this.params.optimizePitch) {
            slots.push("pitch");
            x0.push(pitch);
            stepInit.push(deg2rad(0.5));
        }
        if (this.params.optimizeRoll) {
            slots.push("roll");
            x0.push(roll);
            stepInit.push(deg2rad(0.5));
        }
        if (this.params.optimizeHeight) {
            slots.push("height");
            x0.push(height);
            stepInit.push(this.params.heightM * 0.05);
        }

        const unpack = (vec: number[]): { y: number; p: number; r: number; h: number } => {
            let y = yaw, p = pitch, r = roll, h = height;
            for (let i = 0; i < slots.length; i++) {
                const v = vec[i];
                switch (slots[i]) {
                    case "yaw": y = v; break;
                    case "pitch": p = v; break;
                    case "roll": r = v; break;
                    case "height": h = v; break;
                }
            }
            return { y, p, r, h };
        };

        const lossFn = (vec: number[]): number => {
            const { y, p, r, h } = unpack(vec);
            if (h < heightLo || h > heightHi) return Number.POSITIVE_INFINITY;
            if (y < yawLo || y > yawHi) return Number.POSITIVE_INFINITY;
            return evalAt(y, p, r, h).loss;
        };

        console.log("[Fit] NM start", {
            slots,
            x0Display: x0.map((v, i) => slots[i] === "height" ? `${v.toFixed(3)} m` : `${r2d(v).toFixed(3)}°`),
            stepInitDisplay: stepInit.map((v, i) => slots[i] === "height" ? `${v.toFixed(3)} m` : `${r2d(v).toFixed(3)}°`),
            initialLoss: lossFn(x0),
        });

        let xStar = this.nelderMead(x0, stepInit, lossFn);

        // Polish step: refine the Huber optimum by minimizing pure squared
        // error over the Huber inlier set (frozen). Huber's linear-in-e
        // contribution from outliers introduces a small bias that pulls the
        // optimum slightly off the inlier-RMSE minimum, which is what the
        // live UI shows. Freezing the inlier set and switching to L2 drives
        // the gradient of inlier-RMSE to zero at the final pose, so the user
        // can't find a lower RMSE by tweaking parameters by hand.
        const huberFit = unpack(xStar);
        const huberEval = evalAt(huberFit.y, huberFit.p, huberFit.r, huberFit.h);
        if (huberEval.inlierIdx.length >= 20) {
            const frozenInliers = huberEval.inlierIdx.slice();
            const polishLoss = (vec: number[]): number => {
                const { y, p, r, h } = unpack(vec);
                if (h < heightLo || h > heightHi) return Number.POSITIVE_INFINITY;
                if (y < yawLo || y > yawHi) return Number.POSITIVE_INFINITY;
                let sum = 0;
                let count = 0;
                for (const idx of frozenInliers) {
                    const s = this.samples[idx];
                    const proj = this.projectRadarPointToImage(s.Pw, y, p, r, h);
                    if (!proj) continue;
                    const du = proj.u - s.uMeas;
                    const dv = proj.v - s.vMeas;
                    sum += du * du + dv * dv;
                    count++;
                }
                return count > 0 ? sum / count : Number.POSITIVE_INFINITY;
            };

            const polishSteps = stepInit.map((s) => s * 0.25);
            const xPolished = this.nelderMead(xStar, polishSteps, polishLoss, 200, 1e-12);
            const beforePolishL2 = polishLoss(xStar);
            const afterPolishL2 = polishLoss(xPolished);
            console.log("[Fit] polish", {
                frozenInliers: frozenInliers.length,
                beforeRmsePx: Math.sqrt(beforePolishL2),
                afterRmsePx: Math.sqrt(afterPolishL2),
            });
            if (afterPolishL2 < beforePolishL2) {
                xStar = xPolished;
            }
        }

        const fit = unpack(xStar);
        yaw = fit.y;
        pitch = this.params.optimizePitch ? fit.p : this.params.pitch0Rad;
        roll = this.params.optimizeRoll ? fit.r : this.params.roll0Rad;
        height = this.params.optimizeHeight ? fit.h : this.params.heightM;

        console.log("[Fit] NM done", {
            xStarDisplay: xStar.map((v, i) => slots[i] === "height" ? `${v.toFixed(3)} m` : `${r2d(v).toFixed(3)}°`),
            finalLoss: lossFn(xStar),
            unpackedYawDeg: r2d(yaw),
            unpackedPitchDeg: r2d(pitch),
            unpackedRollDeg: r2d(roll),
            unpackedHeightM: height,
        });

        // Final evaluation & report (inliers only)
        const finalEval = evalAt(yaw, pitch, roll, height);
        if (!isFinite(finalEval.loss) || finalEval.inlierIdx.length === 0) return null;

        const inlierErrors = finalEval.inlierIdx.map((i) => finalEval.errs[i]);
        const rmse = Math.sqrt(inlierErrors.reduce((acc, e) => acc + e * e, 0) / inlierErrors.length);
        const mean = inlierErrors.reduce((acc, e) => acc + e, 0) / inlierErrors.length;
        const med = median(inlierErrors);
        const maxE = Math.max(...inlierErrors);

        // Update internal estimate. NOTE: we deliberately do NOT write `height`
        // back to `this.params.heightM` — the bracket [heightM*0.8, heightM*1.2]
        // must stay anchored to the user's entered height across iterations.
        // Otherwise pitch/height coupling drives height to the lower bound,
        // which shrinks the bracket, which lets it drift further the next
        // iteration. The fitted height is returned in the report instead.
        this.yawRad = yaw;
        this.pitchRad = pitch;
        this.rollRad = roll;

        this.isFit = true;

        console.log("[Fit] final", {
            yawDeg: r2d(yaw),
            pitchDeg: r2d(pitch),
            rollDeg: r2d(roll),
            heightM: height,
            rmsePx: rmse,
            meanPx: mean,
            medianPx: med,
            maxPx: maxE,
            usedSamples: inlierErrors.length,
            droppedAsOutliers: Math.max(0, finalEval.errs.length - inlierErrors.length),
            totalSamples: this.samples.length,
        });

        return {
            usedSamples: inlierErrors.length,
            droppedAsOutliers: Math.max(0, finalEval.errs.length - inlierErrors.length),
            rmsePx: rmse,
            meanPx: mean,
            medianPx: med,
            maxPx: maxE,
            yawRad: yaw,
            pitchRad: pitch,
            rollRad: roll,
            heightM: height,
            errorsPx: inlierErrors,
        };
    }

    /**
     * Nelder-Mead simplex optimizer (derivative-free). Builds an n+1 vertex
     * simplex around x0 using the per-dimension initialStep, then iterates
     * reflection / expansion / contraction / shrink until the value spread
     * across the simplex falls below tol or maxIter is hit.
     */
    private nelderMead(
        x0: number[],
        initialStep: number[],
        fn: (x: number[]) => number,
        maxIter = 400,
        tol = 1e-9
    ): number[] {
        const n = x0.length;
        type Vertex = { x: number[]; v: number };

        const verts: Vertex[] = [{ x: x0.slice(), v: fn(x0) }];
        for (let i = 0; i < n; i++) {
            const x = x0.slice();
            x[i] += initialStep[i];
            verts.push({ x, v: fn(x) });
        }

        const alpha = 1;    // reflection
        const gamma = 2;    // expansion
        const rho = 0.5;    // contraction
        const sigma = 0.5;  // shrink

        for (let iter = 0; iter < maxIter; iter++) {
            verts.sort((a, b) => a.v - b.v);
            const best = verts[0];
            const secondWorst = verts[n - 1];
            const worst = verts[n];

            if (iter % 50 === 0) {
                console.log(`[Fit] NM iter ${iter}`, {
                    bestLoss: best.v,
                    worstLoss: worst.v,
                    spread: worst.v - best.v,
                    bestX: best.x.map((v) => Number(v.toFixed(6))),
                });
            }

            if (isFinite(best.v) && isFinite(worst.v) && worst.v - best.v < tol) {
                console.log(`[Fit] NM converged at iter ${iter}`, { bestLoss: best.v, spread: worst.v - best.v });
                break;
            }

            // Centroid of all vertices except the worst.
            const centroid = new Array<number>(n).fill(0);
            for (let i = 0; i < n; i++) {
                for (let j = 0; j < n; j++) centroid[j] += verts[i].x[j];
            }
            for (let j = 0; j < n; j++) centroid[j] /= n;

            // Reflection
            const xr = centroid.map((c, j) => c + alpha * (c - worst.x[j]));
            const vr = fn(xr);

            if (vr < secondWorst.v && vr >= best.v) {
                verts[n] = { x: xr, v: vr };
                continue;
            }

            if (vr < best.v) {
                // Expansion
                const xe = centroid.map((c, j) => c + gamma * (xr[j] - c));
                const ve = fn(xe);
                verts[n] = ve < vr ? { x: xe, v: ve } : { x: xr, v: vr };
                continue;
            }

            // Contraction
            const xc = centroid.map((c, j) => c + rho * (worst.x[j] - c));
            const vc = fn(xc);
            if (vc < worst.v) {
                verts[n] = { x: xc, v: vc };
                continue;
            }

            // Shrink toward best
            const bx = best.x;
            for (let i = 1; i < n + 1; i++) {
                const x = verts[i].x.map((xj, j) => bx[j] + sigma * (xj - bx[j]));
                verts[i] = { x, v: fn(x) };
            }
        }

        verts.sort((a, b) => a.v - b.v);
        return verts[0].x;
    }

    private linspace(a: number, b: number, n: number): number[] {
        if (n <= 1) return [a];
        const step = (b - a) / (n - 1);
        return Array.from({ length: n }, (_, i) => a + i * step);
    }

    private centroid(points: { u: number; v: number }[]) {
        if (points.length === 0) {
            throw new Error("No points");
        }

        let sumU = 0;
        let sumV = 0;

        for (const p of points) {
            sumU += p.u;
            sumV += p.v;
        }

        return {
            u: sumU / points.length,
            v: sumV / points.length,
        };
    }

    public radarPointToImageEstimate(
        x: number,
        y: number,
        z: number,
        dAzRad = 1.5 * Math.PI / 180,
        beamHalfRad = 30 * Math.PI / 180,
        dR = .5,            // meters (tune)
        zMin = 1,           // ground
        zMax = 3.0            // vehicle top-ish (tune)
    ): { u: number, v: number } {
        const range = Math.sqrt(x * x + y * y + z * z);
        const az = Math.atan2(x, y); // azimuth in device frame

        // device -> world rotation from IMU attitude
        // apply pitch (about X) then roll (about Y)
        const R_world_from_device = matMul3(Ry(this.rollRad), Rx(this.pitchRad));

        // sample boundary combinations (you can densify elevation for nicer polygons)
        const azSamples = [az - dAzRad, az + dAzRad];
        const elSamples = this.linspace(-beamHalfRad, +beamHalfRad, 64);
        const rSamples = [Math.max(0.1, range - dR), range + dR];

        const imgPts: { u: number, v: number }[] = [];

        for (const azS of azSamples) {
            for (const elS of elSamples) {
                // direction in device frame (unit)
                const cosEl = Math.cos(elS);
                const dirDev: Vec3 = {
                    x: Math.sin(azS) * cosEl,
                    y: Math.cos(azS) * cosEl,
                    z: Math.sin(elS),
                };

                // rotate into world frame
                const dirW = mulMat3Vec3(R_world_from_device, dirDev);

                for (const rS of rSamples) {
                    // point in world: origin at (0,0,height) + r*dir
                    const pW: Vec3 = {
                        x: rS * dirW.x,
                        y: rS * dirW.y,
                        z: this.params.heightM + rS * dirW.z,
                    };

                    // constrain by plausible height band
                    if (pW.z < zMin || pW.z > zMax) continue;

                    // IMPORTANT: projection is done in CAMERA frame.
                    // Since camera and radar share the device frame, we can project using the
                    // device ray point in device frame (not world). But we used world only for filtering.
                    // Use the corresponding point in device frame at range rS:
                    const pDev: Vec3 = { x: rS * dirDev.x, y: rS * dirDev.y, z: rS * dirDev.z };

                    // device -> camera
                    const pCam = mulMat3Vec3(this.R_cam_from_world_axes, pDev);

                    const pix = projectCamToPixelDistorted(pCam, this.intr, this.dist);
                    if (pix) imgPts.push(pix);
                }
            }
        }

        if (!imgPts || imgPts.length === 0) return { u: 0, v: 0 };
        const centroid = this.centroid(imgPts);
        return centroid;
    }

    // ---------- Core projection: world point -> camera -> pixel ----------
    public projectRadarPointToImage(Pw: Vec3, yaw: number, pitch: number, roll: number, heightM: number): { u: number; v: number } | null {
        // Camera center in world (above ground)

        const Cw: Vec3 = { x: 0, y: 0, z: heightM };

        // World -> device attitude: we build WorldFromDevice and invert.
        // WorldFromDevice = Rz(yaw) * Rx(pitch) * Ry(roll)
        const R_world_from_device = matMul3(matMul3(Rz(yaw), Rx(pitch)), Ry(roll));
        const R_device_from_world = transpose3(R_world_from_device);

        // Point relative to camera center, in world
        const dW: Vec3 = { x: Pw.x - Cw.x, y: Pw.y - Cw.y, z: Pw.z - Cw.z };

        // Rotate world vector into device coords
        const pDev = mulMat3Vec3(R_device_from_world, dW);

        // Remap device/world axes to camera axes (X right, Y down, Z forward)
        const pCam = mulMat3Vec3(this.R_cam_from_world_axes, pDev);

        // Project with intrinsics + distortion
        return projectCamToPixelDistorted(pCam, this.intr, this.dist);
    }

    // ---------- Inverse projection: image -> world ground ----------
    public projectImagePointToRadar(u: number, v: number, yaw: number, pitch: number, roll: number, heightM: number): Vec3 | null {
        const xd = (u - this.intr.cx) / this.intr.fx;
        const yd = (v - this.intr.cy) / this.intr.fy;
        const undistorted = undistortNormalized(xd, yd, this.dist);
        if (!undistorted) return null;

        const rayCam: Vec3 = { x: undistorted.x, y: undistorted.y, z: 1 };

        const R_world_from_device = matMul3(matMul3(Rz(yaw), Rx(pitch)), Ry(roll));
        const R_device_from_cam = transpose3(this.R_cam_from_world_axes);
        const rayWorld = mulMat3Vec3(R_world_from_device, mulMat3Vec3(R_device_from_cam, rayCam));

        if (!isFinite(rayWorld.z) || Math.abs(rayWorld.z) < 1e-6) return null;

        const Cw: Vec3 = { x: 0, y: 0, z: heightM };
        const t = -Cw.z / rayWorld.z;
        if (!isFinite(t) || t <= 0) return null;

        return { x: Cw.x + t * rayWorld.x, y: Cw.y + t * rayWorld.y, z: 0 };
    }
}
